import { Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ASSET_NAME, Flows, TicketType } from '../app.consts';
import {
  AddApplicationResponseModel,
  GetApplicationResponseModel,
} from '../models/applications.model';
import { reasonOptions } from 'src/configs/dropdown-options/options.se';
import { TranslateService } from '@ngx-translate/core';
import { GetLeadResponseModel } from '../models/lead.model';
import {
  excludeGenericFileUploads,
  exclusivelyGenericFileUploads,
} from '../utils/filter-functions';

@Injectable()
export class ApplicationService {
  private _contactId!: number;
  private _companyId!: number;
  private _applicationId!: number;
  private _autoprocessing!: number;
  private _flowId: Flows = Flows.FULL;
  private _customerSource!: string;
  // purpose screen
  private _reason!: string;
  private _reasonDescription!: string;
  // guarantor
  private _selfGuarantor!: boolean;
  private _additionalGuarantors: Array<FormGroup> = [];
  // turnover
  private _estimatedTurnover!: number;
  private _estimatedTurnoverPrev!: number;
  private _estimatedMonthlySales!: number;
  // accounting-upload
  private _balance!: Array<FormControl>;
  private _profitAndLoss!: Array<FormControl>;
  // account-data
  private _accountData!: Array<FormControl>;
  private _klarnaUpload!: boolean;
  // real-estate
  private _realEstate!: number;
  private _propertyDescription!: string;
  private _realEstateFiles!: Array<FormControl>;
  // securities
  private _factoring!: number;
  private _companyMortgage!: number;
  // other-securities
  private _other!: string;
  private _otherSecuritiesFiles!: Array<FormControl>;
  // big-ticket-personal-contact
  private _personalContact!: boolean;
  // iban
  private _iban!: string;
  // generic file
  private _genericFiles!: Array<FormControl>;

  constructor(private translateService: TranslateService) {}

  get contactId(): number {
    return this._contactId;
  }

  set contactId(value: number) {
    this._contactId = value;
  }

  get companyId(): number {
    return this._companyId;
  }

  set companyId(value: number) {
    this._companyId = value;
  }

  get applicationId(): number {
    return this._applicationId;
  }

  set applicationId(value: number) {
    this._applicationId = value;
  }

  get autoprocessing(): number {
    return this._autoprocessing;
  }

  set autoprocessing(value: number) {
    this._autoprocessing = value;
  }

  get flowId(): Flows {
    return this._flowId;
  }

  set flowId(value: Flows) {
    this._flowId = value;
  }

  get customerSource(): string {
    return this._customerSource;
  }

  set customerSource(value: string) {
    this._customerSource = value;
  }

  get reason(): string {
    return this._reason;
  }

  set reason(value: string) {
    this._reason = value;
  }

  get reasonDescription(): string {
    return this._reasonDescription;
  }

  set reasonDescription(value: string) {
    this._reasonDescription = value;
  }

  get selfGuarantor(): boolean {
    return this._selfGuarantor;
  }

  set selfGuarantor(value: boolean) {
    this._selfGuarantor = value;
  }

  get additionalGuarantors(): Array<FormGroup> {
    return this._additionalGuarantors;
  }

  get estimatedTurnover(): number {
    return this._estimatedTurnover;
  }

  set estimatedTurnover(value: number) {
    this._estimatedTurnover = value;
  }

  get estimatedTurnoverPrev(): number {
    return this._estimatedTurnoverPrev;
  }

  set estimatedTurnoverPrev(value: number) {
    this._estimatedTurnoverPrev = value;
  }

  get estimatedMonthlySales(): number {
    return this._estimatedMonthlySales;
  }

  set estimatedMonthlySales(value: number) {
    this._estimatedMonthlySales = value;
  }

  get balance(): Array<FormControl> {
    return this._balance;
  }

  set balance(value: Array<FormControl>) {
    this._balance = value;
  }

  get profitAndLoss(): Array<FormControl> {
    return this._profitAndLoss;
  }

  set profitAndLoss(value: Array<FormControl>) {
    this._profitAndLoss = value;
  }

  get accountData(): Array<FormControl> {
    return this._accountData;
  }

  set accountData(value: Array<FormControl>) {
    this._accountData = value;
  }

  get klarnaUpload(): boolean {
    return this._klarnaUpload;
  }

  set klarnaUpload(value: boolean) {
    this._klarnaUpload = value;
  }

  get realEstate(): number {
    return this._realEstate;
  }

  set realEstate(value: number) {
    this._realEstate = value;
  }

  get propertyDescription(): string {
    return this._propertyDescription;
  }

  set propertyDescription(value: string) {
    this._propertyDescription = value;
  }

  get realEstateFiles(): Array<FormControl> {
    return this._realEstateFiles;
  }

  set realEstateFiles(value: Array<FormControl>) {
    this._realEstateFiles = value;
  }

  get factoring(): number {
    return this._factoring;
  }

  set factoring(value: number) {
    this._factoring = value;
  }

  get companyMortgage(): number {
    return this._companyMortgage;
  }

  set companyMortgage(value: number) {
    this._companyMortgage = value;
  }

  get other(): string {
    return this._other;
  }

  set other(value: string) {
    this._other = value;
  }

  get otherSecuritiesFiles(): Array<FormControl> {
    return this._otherSecuritiesFiles;
  }

  set otherSecuritiesFiles(value: Array<FormControl>) {
    this._otherSecuritiesFiles = value;
  }

  get personalContact(): boolean {
    return this._personalContact;
  }

  set personalContact(value: boolean) {
    this._personalContact = value;
  }

  get iban(): string {
    return this._iban;
  }

  set iban(value: string) {
    this._iban = value;
  }

  get genericFiles(): Array<FormControl> {
    return this._genericFiles;
  }

  set genericFiles(value: Array<FormControl>) {
    this._genericFiles = value;
  }

  public populateServiceWithData(data: AddApplicationResponseModel): void {
    this.contactId = data.contactId;
    this.companyId = data.companyId;
    this.applicationId = data.applicationId;
    this.autoprocessing = data.autoprocessing;
    this.flowId = data.flowId;
  }

  public populateServiceWithGetApplicationResponseData(
    data: GetApplicationResponseModel
  ): void {
    this.applicationId = data.id;
    this.companyId = data.company.id;

    // purpose
    const option = reasonOptions.find((o) => o.display === data.reason);
    this.reason = option
      ? this.translateService.instant(`reasonOptions.${option.display}`)
      : data.reason
      ? data.reason
      : '';
    this.reasonDescription = data.reasonDesc || '';

    // guarantor
    this.selfGuarantor = data.contacts[0].collateral;
    const initialAdditionalGuarantors = data.contacts
      .slice(1)
      .filter((user) => user.collateral)
      .map(
        (user) =>
          new FormGroup({
            email: new FormControl(user.email),
            personNumber: new FormControl(user.personalIdNumber),
            contactId: new FormControl(user.id),
            filled: new FormControl(true),
          })
      );
    initialAdditionalGuarantors.map((guarantor) =>
      this.additionalGuarantors.push(guarantor)
    );

    // turnover
    this.estimatedTurnover = data?.estimatedTurnover;
    this.estimatedTurnoverPrev = data?.estimatedTurnoverPrev;

    // monthly sales
    this.estimatedMonthlySales = data?.estimatedMonthlySales;

    // accounting upload
    this.balance =
      data?.assets?.filter((asset) => asset.name === ASSET_NAME.BALANCE)[0]
        ?.files || [];
    this.profitAndLoss =
      data?.assets?.filter(
        (asset) => asset.name === ASSET_NAME.PROFIT_AND_LOSS
      )[0]?.files || [];

    // account data
    this.accountData =
      data?.assets?.filter((asset) => asset.name === ASSET_NAME.ACCOUNT_DATA)[0]
        ?.files || [];
    this.klarnaUpload =
      data?.openBanking?.accountStatementStatus === 'Completed';

    // real estate
    this.realEstate = data?.securities?.realEstate ? 1 : 2;
    this.propertyDescription = data?.securities?.realEstate || '';
    this.realEstateFiles =
      data?.assets?.filter((asset) => asset.name === ASSET_NAME.REAL_ESTATE)[0]
        ?.files || [];

    // securities
    this.factoring = data?.securities?.factoring;
    this.companyMortgage = data?.securities?.companyMortgage;

    // other securities
    this.other = data?.securities?.other || '';
    this.otherSecuritiesFiles = excludeGenericFileUploads(
      data?.assets?.filter((asset) => asset.name === ASSET_NAME.OTHER)[0]
        ?.files || []
    );

    // personal contact
    this.personalContact = data?.personalContact;

    // Set flow id when ticketType === BIG
    if (data.ticketType === TicketType.BIG) {
      this.flowId = Flows.BIG;
    }

    // iban
    this.iban = data?.company?.iban;

    // generic files
    this.genericFiles = exclusivelyGenericFileUploads(
      data?.assets?.filter((asset) => asset.name === ASSET_NAME.OTHER)[0]
        ?.files || []
    );
  }

  public populateServiceWithGetLeadResponseData(
    data: GetLeadResponseModel
  ): void {
    if (data.estimatedTurnover) this.estimatedTurnover = data.estimatedTurnover;
    if (data.estimatedTurnoverPrev)
      this.estimatedTurnoverPrev = data.estimatedTurnoverPrev;
  }
}
